import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import equal from "fast-deep-equal"
import { validate as isUuid } from "uuid"
import { fromVersionContent, type TemplateWriteAccess } from "./useVersionDraft"
import type {
  Bindings,
  BulkPublicationVersionItem,
  BulkPublicationVersionResult,
  Publication,
  ReadBindings,
  TemplateVersion,
} from "../interface/publications"
import {
  useBulkPublicationVersionMutation,
  useLazyGetPublicationQuery,
} from "../services/publications/publicationsApi"
import { useLazyGetTemplateVersionQuery } from "../services/publications/templatesApi"
import { businessApi, postBotTags } from "../services/publications/api"
import {
  getMissingActiveBindings,
  mergeBindings,
  toBindingWrite,
} from "../utils/bindings"
import { buildPublicationPatch } from "../utils/patches"
import { readPostBotError } from "../utils/apiErrors"

export interface BulkVersionRow {
  publicationId: string
  baseline?: Publication
  remote?: Publication
  templateVersionId: string
  bindings: ReadBindings
  /** Explicit user intent survives repeated GETs even if a value temporarily matches. */
  replacements: Bindings
  version?: TemplateVersion
  loading: boolean
  error?: unknown
  outcome: "draft" | "success" | "failed" | "unknown"
  capturedRevision?: number
  result?: BulkPublicationVersionResult
  reconciled?: boolean
  reviewed: boolean
  attempt?: BulkPublicationVersionItem
  attemptBaseline?: Publication
}
export interface BulkVersionState {
  operationId: number
  status: "idle" | "draft" | "saving" | "results"
  rows: BulkVersionRow[]
  validation: string
  error?: unknown
}
const initialState = (): BulkVersionState => ({
  operationId: 0,
  status: "idle",
  rows: [],
  validation: "",
})

function proposalApplied(
  row: BulkVersionRow,
  remote: Publication,
  version: TemplateVersion,
) {
  return (
    remote.templateVersionId === row.templateVersionId &&
    version.id === row.templateVersionId &&
    Object.entries(row.replacements).every(([id, value]) =>
      equal(remote.bindings[id], value),
    ) &&
    !getMissingActiveBindings(
      fromVersionContent(version.content),
      remote.bindings,
    ).length
  )
}

function itemBody(row: BulkVersionRow): BulkPublicationVersionItem | undefined {
  if (
    !row.baseline ||
    !row.version ||
    row.version.id !== row.templateVersionId ||
    row.loading ||
    row.error ||
    !Number.isSafeInteger(row.baseline.revision) ||
    row.baseline.revision < 1 ||
    getMissingActiveBindings(
      fromVersionContent(row.version.content),
      row.bindings,
    ).length
  )
    return
  try {
    const patch = buildPublicationPatch(row.baseline, {
      templateVersionId: row.templateVersionId,
      bindings: row.bindings,
    })
    if (!patch) return
    return {
      publicationId: row.publicationId,
      revision: row.baseline.revision,
      templateVersionId: row.templateVersionId,
      ...(patch.bindings ? { bindings: patch.bindings } : {}),
    }
  } catch {
    return
  }
}

/** One mounted owner per operation. Hiding the dialog is NOT a reset. No automatic
 * retry: both known failures and unknown writes require fresh GETs and review.
 * Call start explicitly to discard an old operation; never key this hook by selection.
 */
export function useBulkVersionChange(
  options: TemplateWriteAccess & { open: boolean },
) {
  const [state, setState] = useState<BulkVersionState>(initialState)
  const current = useRef(state)
  const access = useRef(options)
  access.current = options
  const mounted = useRef(true)
  const operation = useRef(0)
  const readEpoch = useRef(0)
  const tickets = useRef(new Map<string, symbol>())
  const [fetchPublication] = useLazyGetPublicationQuery()
  const [fetchVersion] = useLazyGetTemplateVersionQuery()
  const [save] = useBulkPublicationVersionMutation()
  const dispatch = useDispatch()
  const update = (transform: (value: BulkVersionState) => BulkVersionState) => {
    if (!mounted.current) return
    current.current = transform(current.current)
    setState(current.current)
  }
  const updateRow = (
    id: string,
    transform: (row: BulkVersionRow) => BulkVersionRow,
  ) =>
    update((value) => ({
      ...value,
      rows: value.rows.map((row) =>
        row.publicationId === id ? transform(row) : row,
      ),
    }))
  const writable = () =>
    access.current.open &&
    !access.current.readOnly &&
    access.current.canWrite !== false
  const editable = (row: BulkVersionRow) =>
    writable() &&
    current.current.status !== "saving" &&
    row.outcome !== "success" &&
    (row.outcome === "draft" || !!row.remote || row.reviewed)
  const ticket = (id: string) => {
    const token = Symbol(id)
    tickets.current.set(id, token)
    const epoch = readEpoch.current
    const op = operation.current
    return () =>
      mounted.current &&
      access.current.open &&
      operation.current === op &&
      readEpoch.current === epoch &&
      tickets.current.get(id) === token
  }
  useEffect(() => {
    mounted.current = true
    // Real unmounts leave this owner inactive. StrictMode's cleanup/setup replay
    // must not invalidate the one operation already started by the dialog.
    return () => {
      mounted.current = false
    }
  }, [])
  useEffect(() => {
    if (!options.open) {
      readEpoch.current++
      update((value) => ({
        ...value,
        rows: value.rows.map((row) =>
          row.loading
            ? { ...row, loading: false, reviewed: false, remote: undefined }
            : row,
        ),
      }))
    }
    // Closing cancels read/review work, not an already submitted mutation's outcome.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.open])

  const loadVersion = async (id: string, versionId: string) => {
    const active = ticket(id)
    updateRow(id, (value) => ({
      ...value,
      templateVersionId: versionId,
      version: undefined,
      loading: !!versionId,
      error: undefined,
      reviewed: false,
    }))
    if (!versionId) return
    try {
      const version = await fetchVersion(versionId, false).unwrap()
      if (version.id !== versionId) throw new Error("Версия недоступна")
      if (active())
        updateRow(id, (value) => ({ ...value, version, loading: false }))
    } catch (error) {
      if (active())
        updateRow(id, (value) => ({ ...value, error, loading: false }))
    }
  }
  const selectVersion = async (id: string, versionId: string) => {
    const row = current.current.rows.find((value) => value.publicationId === id)
    if (!row || !editable(row) || !row.baseline) return
    await loadVersion(id, versionId)
  }
  const load = async (id: string) => {
    const row = current.current.rows.find((value) => value.publicationId === id)
    if (
      !row ||
      row.outcome !== "draft" ||
      !access.current.open ||
      current.current.status === "saving"
    )
      return
    if (row.baseline) {
      await loadVersion(id, row.templateVersionId)
      return
    }
    const active = ticket(id)
    updateRow(id, (value) => ({ ...value, loading: true, error: undefined }))
    try {
      const baseline = await fetchPublication(id, false).unwrap()
      if (!baseline || baseline.id !== id)
        throw new Error("Публикация недоступна")
      if (!active()) return
      updateRow(id, (value) => ({
        ...value,
        baseline,
        bindings: baseline.bindings,
        templateVersionId: baseline.templateVersionId,
      }))
      const version = await fetchVersion(
        baseline.templateVersionId,
        false,
      ).unwrap()
      if (version.id !== baseline.templateVersionId)
        throw new Error("Версия недоступна")
      if (active())
        updateRow(id, (value) => ({ ...value, version, loading: false }))
    } catch (error) {
      if (active())
        updateRow(id, (value) => ({ ...value, error, loading: false }))
    }
  }
  const start = async (ids: readonly string[]) => {
    if (!access.current.open || current.current.status === "saving") return
    if (
      !Array.isArray(ids) ||
      !ids.length ||
      ids.some((id) => !isUuid(id)) ||
      new Set(ids.map((id) => id.toLowerCase())).size !== ids.length
    ) {
      update((value) => ({
        ...value,
        validation: "Выберите непустой список уникальных публикаций.",
      }))
      return
    }
    const op = ++operation.current
    const epoch = ++readEpoch.current
    tickets.current.clear()
    update(() => ({
      operationId: op,
      status: "draft",
      validation: "",
      rows: ids.map((publicationId) => ({
        publicationId,
        templateVersionId: "",
        bindings: {},
        replacements: {},
        loading: false,
        outcome: "draft",
        reviewed: false,
      })),
    }))
    // Sequential individual resources: bounded concurrency, no list-all or invented cap.
    for (const id of ids) {
      if (
        operation.current !== op ||
        readEpoch.current !== epoch ||
        !access.current.open ||
        !mounted.current
      )
        break
      await load(id)
    }
  }
  const setBindings = (id: string, bindings: Bindings) => {
    const row = current.current.rows.find((value) => value.publicationId === id)
    if (!row || !editable(row) || row.loading) return
    try {
      const replacements = Object.fromEntries(
        Object.entries(bindings).map(([id, value]) => [
          id,
          toBindingWrite(value),
        ]),
      )
      const merged = mergeBindings(row.bindings, replacements)
      updateRow(id, (value) => ({
        ...value,
        bindings: merged,
        replacements: { ...value.replacements, ...replacements },
        reviewed: false,
      }))
    } catch {
      update((value) => ({
        ...value,
        validation: "Нужен корректный URL или бот с полными metadata.",
      }))
    }
  }
  const reconcile = async () => {
    if (
      !access.current.open ||
      current.current.status === "saving" ||
      current.current.rows.some((row) => row.loading)
    )
      return
    const op = operation.current
    const epoch = readEpoch.current
    const rows = current.current.rows.filter(
      (row) => row.outcome === "failed" || row.outcome === "unknown",
    )
    for (const { publicationId } of rows) {
      if (
        operation.current !== op ||
        readEpoch.current !== epoch ||
        !access.current.open ||
        !mounted.current
      )
        break
      const row = current.current.rows.find(
        (value) => value.publicationId === publicationId,
      )!
      if (row.outcome === "success") continue
      const active = ticket(row.publicationId)
      updateRow(row.publicationId, (value) => ({
        ...value,
        loading: true,
        version: undefined,
        error: undefined,
        remote: undefined,
        reviewed: false,
      }))
      try {
        const remote = await fetchPublication(row.publicationId, false).unwrap()
        if (
          !remote ||
          remote.id !== row.publicationId ||
          !Number.isSafeInteger(remote.revision) ||
          remote.revision < 1
        )
          throw new Error("Публикация недоступна")
        if (!active()) continue
        // Keep only the user's replacements over the fresh resource; unrelated and
        // inactive server values survive. Unchanged historical botId is never written.
        const bindings = mergeBindings(remote.bindings, row.replacements)
        updateRow(row.publicationId, (value) => ({
          ...value,
          baseline: remote,
          remote,
          bindings,
        }))
        const version = await fetchVersion(
          row.templateVersionId,
          false,
        ).unwrap()
        if (version.id !== row.templateVersionId)
          throw new Error("Версия недоступна")
        if (!active()) continue
        const applied = proposalApplied(row, remote, version)
        updateRow(row.publicationId, (value) => ({
          ...value,
          baseline: remote,
          remote,
          bindings,
          version,
          loading: false,
          ...(applied ? { outcome: "success", reconciled: true } : {}),
        }))
        if (applied)
          dispatch(businessApi.util.invalidateTags([postBotTags.publications]))
      } catch (error) {
        if (active())
          updateRow(row.publicationId, (value) => ({
            ...value,
            error,
            loading: false,
          }))
      }
    }
  }
  const confirm = (id: string) => {
    const row = current.current.rows.find((value) => value.publicationId === id)
    if (
      !row ||
      !editable(row) ||
      !row.remote ||
      !row.version ||
      row.loading ||
      row.error
    )
      return
    if (proposalApplied(row, row.remote, row.version)) {
      updateRow(id, (value) => ({
        ...value,
        outcome: "success",
        reconciled: true,
      }))
      dispatch(businessApi.util.invalidateTags([postBotTags.publications]))
    } else if (itemBody({ ...row, baseline: row.remote })) {
      updateRow(id, (value) => ({
        ...value,
        baseline: value.remote,
        reviewed: true,
      }))
    }
  }
  const ready = () => {
    const pending = current.current.rows.filter(
      (row) => row.outcome !== "success",
    )
    return (
      writable() &&
      current.current.status !== "saving" &&
      pending.length > 0 &&
      pending.every(
        (row) => (row.outcome === "draft" || row.reviewed) && !!itemBody(row),
      )
    )
  }
  const submit = async () => {
    if (!ready()) return
    const items = current.current.rows
      .filter((row) => row.outcome !== "success")
      .map((row) => itemBody(row)!)
    const op = operation.current
    update((value) => ({
      ...value,
      status: "saving",
      validation: "",
      error: undefined,
      rows: value.rows.map((row) => {
        const attempt = items.find(
          (item) => item.publicationId === row.publicationId,
        )
        return attempt
          ? {
              ...row,
              attempt,
              attemptBaseline: row.baseline,
              capturedRevision: attempt.revision,
              reviewed: false,
              remote: undefined,
              result: undefined,
            }
          : row
      }),
    }))
    try {
      const response = await save({ items }).unwrap()
      if (!mounted.current || operation.current !== op) return
      update((value) => ({
        ...value,
        status: "results",
        rows: value.rows.map((row) => {
          if (!items.some((item) => item.publicationId === row.publicationId))
            return row
          const matches = response.results.filter(
            (result) => result.publicationId === row.publicationId,
          )
          const result = matches.length === 1 ? matches[0] : undefined
          if (
            !result ||
            (result.ok &&
              (!Number.isSafeInteger(result.revision) || result.revision < 1))
          )
            return { ...row, outcome: "unknown" }
          return { ...row, result, outcome: result.ok ? "success" : "failed" }
        }),
      }))
    } catch (error) {
      if (!mounted.current || operation.current !== op) return
      // Only canonical general400 guarantees validation BEFORE any write. All
      // other whole-request failures are conservatively uncertain, including 5xx.
      const knownRejected = readPostBotError(error).status === 400
      update((value) => ({
        ...value,
        status: "results",
        error,
        rows: value.rows.map((row) =>
          items.some((item) => item.publicationId === row.publicationId)
            ? { ...row, outcome: knownRejected ? "failed" : "unknown" }
            : row,
        ),
      }))
    }
  }
  return {
    state,
    canSubmit: ready(),
    start,
    load,
    selectVersion,
    setBindings,
    reconcile,
    confirm,
    submit,
  }
}
